import { createContext, useContext } from 'react'
import type { Me } from '../../lib/api'

/**
 * The signed-in session, as the shell fetched it.
 *
 * Exists so a page can check that the data it just received belongs to the workspace the chrome
 * around it is captioned with. The shell fetches `/auth/me` once and keeps the name; a second tab
 * signing into another workspace replaces the shared refresh cookie underneath it, and the next
 * fetch on this page returns a different tenant's findings while the breadcrumb still says the
 * old one. Nothing here is an authorisation check — the server decided that — it is a check that
 * two server answers describe the same tenant before they are rendered as one screen.
 */
export type SessionState =
  | { state: 'loading' }

  /** The server answered: this is who you are. `me` is null only for a signed-out visitor. */
  | { state: 'known'; me: Me | null }

  /** The lookup itself failed — network, 500, anything that is not an answer. */
  | { state: 'unknown' }

export const SessionContext = createContext<SessionState>({ state: 'loading' })

export function useSession() {
  return useContext(SessionContext)
}

/**
 * Whether a response belongs to the session the shell is showing.
 *
 * Unknown counts as agreement: before `/auth/me` answers there is nothing to disagree with, and
 * refusing to render during that window would make every page flash a warning on load.
 */
export function agrees(session: SessionState, workspaceId: string | undefined): boolean {
  // Nothing to disagree with yet. Refusing during this window would flash a warning on every load.
  if (session.state === 'loading') return true

  // The lookup failed, so we do not know whose data this is. Previously this collapsed into the
  // same `null` a signed-out visitor produced and counted as agreement -- a transient 500 on
  // /auth/me rendered a tenant's findings under a shell captioned "No workspace", which is the
  // identity binding failing open in the one situation it exists for.
  if (session.state === 'unknown') return false

  if (!workspaceId || !session.me?.workspaceId) return true

  return session.me.workspaceId === workspaceId
}

export const SESSION_UNKNOWN =
  'We could not confirm which workspace this session belongs to, so this page is not showing ' +
  'findings. Reload the page.'

/** The signed-in person, when the server actually answered. */
export function personOf(session: SessionState): Me | null {
  return session.state === 'known' ? session.me : null
}

export const SESSION_CHANGED =
  'Your session now belongs to a different workspace — most likely you signed in elsewhere in ' +
  'another tab. Reload the page to see that workspace.'
